import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import LabelInput from "./labelInput";
import { assetsPath } from "../utils/assetsPath";
import useAppStateStore from "../store/AppStateStore";

const AddressLookup = ({
  register,
  watch,
  setValue,
  trigger,
  errors,
  labelHouse = "House name or number",
  labelPostCode = "Postcode",
  STATE,
}) => {
  const [addressData, setAddressData] = useState(null);
  const [allAddresses, setAllAddresses] = useState([]);
  const [errorAddress, setErrorAddress] = useState("");
  const [loader, setLoader] = useState(false);

  const address = watch("address");
  const postcode = watch("postcode");

  // Options for address selection
  const options = allAddresses.map((address) => ({
    value: JSON.stringify(address),
    label: `${address.house}, ${address.street}, ${address.city}, ${address.postcode}`,
  }));

  const handleAddressLookup = async () => {
    const setLookupAddress = STATE.setLookupAddress;
    setValue("lookUpAddress", undefined);
    setLookupAddress(null);

    if (!address) {
      alert("Please enter house no");
      return;
    }
    if (!postcode) {
      alert("Please enter postcode");
      return;
    }

    setLoader(true);

    try {
      const response = await axios.get("/api/address-lookup", {
        params: { postcode, address },
      });

      if (response.status !== 200) {
        throw new Error(
          `Network response was not ok: ${response.statusText ||
            response.status}`
        );
      }

      const data = response.data;

      if (data.addressFound !== null) {
        setAddressData(data.addressFound);
        setAllAddresses([]);
        setValue("lookUpAddress", data.addressFound);
        setLookupAddress(data.addressFound);
        setErrorAddress("");
      } else if (data.addresses !== null) {
        setAllAddresses(data.addresses);
      } else {
        throw new Error(
          "No addresses found provide correct house no and postcode"
        );
      }
    } catch (error) {
      setErrorAddress(error.response?.data?.message || error.message);
      setValue("lookUpAddress", undefined);
      setLookupAddress(null);
      setAllAddresses([]);
      setAddressData(null);
    } finally {
      setLoader(false);
    }
  };

  const handleSelectChange = (selectedOption) => {
    if (selectedOption && selectedOption.value) {
      const selectedValue = JSON.parse(selectedOption.value);

      // Set the selected address data
      setAddressData(selectedValue);

      // Update the form value
      setValue("lookUpAddress", selectedValue);

      // Clear any error messages
      setErrorAddress("");

      // Retrigger validation for "lookUpAddress"
      trigger("lookUpAddress");
    }
  };

  const handleManualOrLookupAdress = () => {
    if (!STATE.enterAddress) {
      STATE.setEnterAddress(true);
      STATE.setShowAddressLookupbtn(false);
    } else {
      STATE.setEnterAddress(false);
      STATE.setShowAddressLookupbtn(true);
    }
  };

  const handleConfirmAddress = () => {
    const manualAddress = {
      house: watch("manualAddress"),
      street: watch("manualSecondAddress") || "",
      city: watch("city"),
      postcode: watch("postcode"),
    };
    setValue("lookUpAddress", manualAddress);
    setErrorAddress("");
    trigger("lookUpAddress");
    STATE.setEnterAddress(false);
    STATE.setShowAddressLookupbtn(true);
  };

  useEffect(() => {
    if (address !== undefined || postcode !== undefined) {
      setAddressData(null);
      setAllAddresses([]);
    }
  }, [address, postcode]);

  return (
    <div className="address-component">
      {!STATE.enterAddress && (
        <>
          <LabelInput
            id="address"
            label={labelHouse}
            type="text"
            placeholder="e.g 12"
            register={register("address", {
              required: "Required",
            })}
            errors={errors.address}
            watch={address}
            trigger={trigger}
          />

          <LabelInput
            id="postcode"
            label={labelPostCode}
            type="text"
            placeholder="e.g M50 3YJ"
            register={register("postcode", {
              required: "Required",
            })}
            errors={errors.postcode}
            watch={postcode}
            trigger={trigger}
          />

          <LabelInput
            id="lookUpAddress"
            className="hiddenInput"
            name="lookUpAddress"
            type="text"
            register={register("lookUpAddress", {
              validate: (value) =>
                !postcode ||
                value !== "" ||
                "This field is required if Vehicle Registration has a value",
            })}
          />

          {loader && <div className="loader"></div>}

          {errorAddress && <span className="errorField">{errorAddress}</span>}

          {allAddresses.length > 0 && (
            <>
              <Select
                onChange={handleSelectChange}
                options={options}
                placeholder="Select Address"
              />
              {errors.lookUpAddress && (
                <span className="errorField">Required</span>
              )}
            </>
          )}
        </>
      )}

      {STATE.enterAddress && (
        <>
          <LabelInput
            id="manualAddress"
            label="Address line 1"
            type="text"
            placeholder="Enter address 1"
            register={register("manualAddress", {
              required: "Required",
            })}
            errors={errors.manualAddress}
            watch={watch("manualAddress")}
            trigger={trigger}
          />
          <LabelInput
            id="manualSecondAddress"
            label="Address line 2 (optional)"
            type="text"
            placeholder="Enter address 2"
            register={register("manualSecondAddress")}
            trigger={trigger}
          />
          <LabelInput
            id="city"
            label="City"
            type="text"
            placeholder="e.g London"
            register={register("city", { required: "Required" })}
            errors={errors.city}
            watch={watch("city")}
            trigger={trigger}
          />
          <LabelInput
            id="county"
            label="County"
            type="text"
            placeholder="e.g Greater London"
            register={register("county", {
              required: "Required",
            })}
            errors={errors.county}
            watch={watch("county")}
            trigger={trigger}
          />
        </>
      )}

      {watch("lookUpAddress") && (
        <>
          <div className="address-label margin-top-15">
            <p className="AddHeading margin-top-5">Your Address</p>
            <img src={assetsPath.tick} alt="Tick Icon" className="" />
          </div>
          <p className="word-break">
            {watch("lookUpAddress")?.house ||
              watch("lookUpAddress")?.houseNo ||
              ""}
            , {watch("lookUpAddress")?.street || ""},{" "}
            {watch("lookUpAddress")?.city || ""},{" "}
            {watch("lookUpAddress")?.postcode || 0}
          </p>
        </>
      )}

      <div className="addressButton">
        {STATE.showAddressLookupbtn && !STATE.enterAddress ? (
          <div>
            <button
              type="button"
              className="lookupBtn bg-black"
              onClick={handleAddressLookup}
            >
              Look up address
            </button>
          </div>
        ) : (
          <div>
            <button
              type="button"
              className="lookupBtn bg-black"
              onClick={handleConfirmAddress}
            >
              Confirm Address
            </button>
          </div>
        )}

        <div className="lookbtnDiv">
          <button
            onClick={handleManualOrLookupAdress}
            type="button"
            className="lookupBtn bg-gray"
          >
            {!STATE.enterAddress
              ? "Enter address manually"
              : "Automatic lookup"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddressLookup;
